import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { FieldExpression, toString as luceneToString } from 'lucene';

import { Guarantee } from '../models/guarantee';
import { CLAIM_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { OrderBy } from '../../shared/utils/constant';
import {
	AttachmentCategory,
	ATTACHMENT_CATEGORY_OTHER,
	GuaranteeStatus
} from '../models/guarantee-constants';
import { BlobService } from '../../shared/utils/blob.service';
import { ConnectionService } from '../../shared/services/connection.service';
import { ErrorService } from '../../shared/services/error.service';
import { TranslationService } from '../../shared/services/translation.service';
import { GuaranteeRequestAttachment } from '../models/guarantee-request-attachment';
import { Part } from '../models/part';

const GUARANTEE_V1_URL = `${ CLAIM_SERVER_URL }/v1/guaranteeRequests`;

interface AttachmentTypeUpdateResponse {
	code: string;
	type: AttachmentCategory;
	versionId: string;
}

interface AttachmentTypeUpdateResult extends AttachmentTypeUpdateResponse {
	attachments?: GuaranteeRequestAttachment[];
	refreshError?: unknown;
}

interface GuaranteeNameUniquenessResponse {
	success: boolean;
	error: string;
	result: boolean;
}

type GuaranteeRequestField = keyof Guarantee
	| `parts.${ keyof Guarantee['parts'][number] }`;

export const REQUEST_FIELDS: GuaranteeRequestField[] = [
	'id',
	'guaranteeId',
	'certificateId',
	'applicationType',
	'requesterName',
	'createdDate',
	'lastModifiedDate',
	'requestDate',
	'name',
	'soldTo',
	'status',
	'dataOnTheProjectSize',
	'magnelisTonnage',
	'mspCompanyName',
	'epcCompanyName',
	'requestDate',
	'newSolarplantCompleteConstruction',
	'extensionExistingSolarplant',
	'retrofitting',
	'name',
	'address',
	'latitude',
	'longitude',
	'ruralAreaDistantFromCoast',
	'proximityBuildingAnimals',
	'distanceToSea',
	'urbanOrIndustrialArea',
	'mainlyComprisingRefinerie',
	'hazardousIndustryDistance',
	'typeOfIndustry',
	'typeOfEmissions',
	'gasCarriedWinds',
	'inDesert',
	'desertComment',
	'coastalMarineEnvironment',
	'lessThan300mfromCoast',
	'withoutWettingSeaWater',
	'partialWettingSeaWater',
	'between300m2kmfromCoast',
	'between2km10kmfromCoast',
	'specificEnvironment',
	'distancetoLakeRiver',
	'upperStructureGuarantee',
	'magnelisZM310',
	'magnelisZM430',
	'magnelisZM620',
	'guaranteeDurationRequested',
	'polesGuarantee',
	'polesMagnelisZM430',
	'polesMagnelisZM620',
	'poleGuaranteeDurationRequested',
	'soilAnalysis',
	'entireMagnelisStructure',
	'stainlessSteelParts',
	'aluminiumParts',
	'stainlessSteel',
	'zincCoated',
	'foundationsConcrete',
	'otherCoatingsSteel',
	'aluminium',
	'magnelisContactPartsCoating',
	'magnelisContactPartsOtherMaterial',
	'customerAdditionalInformation',
	'parts.number',
	'parts.name',
	'parts.sheetThickness',
	'parts.magnelisCoatingMass',
	'parts.corrosion',
	'parts.fabricationProcess',
	'parts.epassivationEasyfilm',
	'newBuildingCompleteConstruction',
	'extensionExistingBuilding',
	'retrofittingRepairExistingBuilding',
	'roofing',
	'wallFacade',
	'compositeFloor',
	'perforatedroofSheet',
	'perSheetRich',
	'coldFormedStrut',
	'rainWaterSystemsGutters',
	'residentialUse',
	'agricultural',
	'industrial',
	'otherADB',
	'otherADBSpecify',
	'acitivityInsideBuilding',
	'presenceofChemical',
	'natureContent',
	'presenceofFermentation',
	'natureContent2',
	'cattle',
	'pigs',
	'polutry',
	'otherAnimals',
	'gasvaporNatureContent',
	'animalsInsideBuilding',
	'densityofAnimals',
	'heatingType',
	'qiaExtractor',
	'qiaChimney',
	'qiaNaturalventilation',
	'qiaForcedventilation',
	'otherEquipment',
	'specifyOtherEquipment',
	'qiacondriskbuilding',
	'humiditySpec',
	'manureManagementGrantings',
	'manureManagementWOGrantings',
	'animalDistance',
	'suggestedCoatingMass',
	'toaSilo',
	'siloBase',
	'siloProductsStored',
	'linerInSilo',
	'siloRoof',
	'siloCylinder',
	'siloInContactSoilConcrete',
	'toaRoadResSystem',
	'rrsHorizontal',
	'rrsMiscellaneous',
	'rrsMagnelisWelded',
	'rrsPoles',
	'toaAcousticBarrier',
	'acoRoadTraffic',
	'acoRailroad',
	'acoConcreteBase',
	'toaPerforatedSheet',
	'peforatedSheet',
	'animalDistance',
	'suggestedCoatingMass',
	'isTOAOtherApps',
	'toaOtherApplication',
	'perPerRich',
	'perApp',
	'isOtherCoatingMass',
	'otherCoatingMass',
	'recordType',
	'gasVapour',
	'buildingHeated',
	'industrialBuilding',
	'agriculturalBuilding',
	'reason',
	'natureMaterial',
	'conditionMaterial',
	'ageMaterial',
	'buildingHumidityRate',
	'storageFodder',
	'storageFertilizer',
	'breedingAnimals',
	'animalsinShed',
	'concreteGratingDuckboard',
	'noDuckboard',
	'naturalVentilationWall',
	'naturalVentilationRoof',
	'forcedVentilation',
	'otherVentilation',
	'otherVentilationSpecify',
	'airRenewal',
	'ammonia',
	'hydrogenSulphide',
	'humidity',
	'buildingEnvAltitude',
	'buildingEnvBLMoreThan20KMFromCoast',
	'buildingEnvBLSpecifyDistance',
	'buildingEnvBLPleaseSpecify',
	'typeOfGuaGrOutdoor',
	'typeofGuaGrIndoor',
	'outdoorGuaranteeDurationExpected',
	'indoorGuaranteeDurationExpected',
	'typeOfGUAOpenBuildingYes',
	'typeOfGUAOpenBuildingYesSpecify',
	'ocRoofing',
	'roofingDeckingRoof',
	'roofingDRSubstrateThickness',
	'roofingDRDesiredCoating',
	'roofingDRDesiredBackCoating',
	'roofingDRMetallicCoatingWeight',
	'roofingDRColor',
	'roofingDRSurface',
	'roofingDRRoofslope',
	'roofingSingleSkinRoof',
	'roofingSSRSubstrateThickness',
	'roofingSSRDesiredCoating',
	'roofingSSRDesiredBackCoating',
	'roofingSSRMetallicCoatingWeight',
	'roofingSSRColor',
	'roofingSSRSurface',
	'roofingSSRRoofslope',
	'roofingDoubleSkinRoof',
	'roofingDsresSubstrateThickness',
	'roofingDsresDesiredCoating',
	'roofingDsresDesiredBackCoating',
	'roofingDsresMetallicCoatingWeight',
	'roofingDsrEsColor',
	'roofingDsrisSubstrateThickness',
	'roofingDsrisDesiredCoating',
	'roofingDsrisDesiredBackCoating',
	'roofingDsrisMetallicCoatingWeight',
	'roofingDsrIsColor',
	'roofingDSRSurface',
	'roofingDSRRoofslope',
	'roofingSandwichPanelsBuiltUpSys',
	'roofingSPESSubstrateThickness',
	'roofingSPESDesiredCoating',
	'roofingSPESDesiredBackCoating',
	'roofingSPESMetallicCoatingWeight',
	'roofingSPESColor',
	'roofingSPISSubstrateThickness',
	'roofingSPISDesiredCoating',
	'roofingSPISDesiredBackCoating',
	'roofingSPISMetallicCoatingWeight',
	'roofingSPISColor',
	'roofingSPSurface',
	'roofingSPRoofslope',
	'roofingSPInsulationPU',
	'roofingSPInsulationPIR',
	'roofingSPInsulationMineralWool',
	'roofingOverlapping',
	'roofingPerforationYes',
	'roofingPerforationYesSpecify',
	'roofingPVSolarRoof',
	'roofingAntiCondensationFelt',
	'clad',
	'cladSingleSkin',
	'cladSSSubstrateThickness',
	'cladSSDesiredCoating',
	'cladSSDesiredBackCoating',
	'cladSSMetallicCoatingWeight',
	'cladSSColor',
	'cladSSSurface',
	'cladDoubleSkin',
	'cladDSESSubstrateThickness',
	'cladDSESDesiredCoating',
	'cladDSESDesiredBackCoating',
	'cladDSESMetallicCoatingWeight',
	'cladDSESColor',
	'cladDSESSurface',
	'cladDSISSubstrateThickness',
	'cladDSISDesiredCoating',
	'cladDSISDesiredBackCoating',
	'cladDSISMetallicCoatingWeight',
	'cladDSISColor',
	'cladDSISSurface',
	'cladSandwichPanelsBuiltUpSys',
	'cladSPESSubstrateThickness',
	'cladSPESDesiredCoating',
	'cladSPESDesiredBackCoating',
	'cladSPESMetallicCoatingWeight',
	'cladSPESColor',
	'cladSPISSubstrateThickness',
	'cladSPISDesiredCoating',
	'cladSPISDesiredBackCoating',
	'cladSPISMetallicCoatingWeight',
	'cladSPISColor',
	'cladSPSurface',
	'cladSPInsulationPU',
	'cladSPInsulationPIR',
	'cladSPInsulationMineralWool',
	'cladOverlapping',
	'cladPerforationYes',
	'cladPerforationYesSpecify',
	'cladDirectionOfInstallation',
	'cladVertical',
	'othersAntiCondensationFelt',
	'othersSpecifyAntiCondensationFelt',
	'omuRoofCladmadeWithProduct',
	'omuUseofMaterialCementBreezeblocks',
	'omuInRoofing',
	'omuInCladding',
	'polesMagnelisZM800',
	'installationType',
	'guaranteeForFloatingSolarMagnelis',
	'floatingSolarMagnelisZM430',
	'floatingSolarMagnelisZM620',
	'floatingSolarMagnelisZM800',
	'guaranteeDurationRequestedForFoating',
	'floatingSolarInstallationAnalysis'
];

@Injectable({ providedIn: 'root' })
export class GuaranteeRequestService {
	guarantee = new Guarantee();

	constructor(
		private readonly http: HttpClient,
		private readonly blobService: BlobService,
		private readonly connectionService: ConnectionService,
		private readonly errorService: ErrorService,
		private readonly translationService: TranslationService,
	) {}

	async saveGuarantee(guarantee: Guarantee) {
		const guaranteeId = guarantee.id;
		if (guarantee.id) {
			await this.uploadAttachments(guarantee.id, this.guarantee).catch((err: unknown) => {
				console.error('unable to upload attachments', err);
			});
		}
		const url = guaranteeId ? new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }`) : new URL(GUARANTEE_V1_URL);
		const headers = new HttpHeaders().set('Content-Type', 'application/json');

		const guaranteeRequest = {
			...guarantee,
			id: undefined,
			guaranteeId: undefined,
			createdDate: undefined,
			lastModifiedDate: undefined,
			attachments: undefined,
			contactParts: undefined,
			contactOtherParts: undefined,
			activityBuilding: undefined,
			animalsBuilding: undefined,
			manureManagement: undefined,
			discharge: undefined
		};

		const response = await (
			!guaranteeId
				? firstValueFrom(this.http.post<Guarantee>(url.toString(), guaranteeRequest, { headers }))
				: firstValueFrom(this.http.put<Guarantee>(url.toString(), guaranteeRequest, { headers }))
		);
		if (response.id) {
			await this.uploadAttachments(response.id, guarantee).catch((err: unknown) => {
				console.error('unable to upload attachments', err);
			});
			if (guarantee.status === GuaranteeStatus.DRAFT) {
				await this.syncAttachmentTypeChanges(response.id, guarantee.attachments);
			}
		}
		const savedGuarantee = response.id && !response.guaranteeId
			? await firstValueFrom(this.getGuaranteeById(response.id))
			: response;
		// reset current guarantee (ensure reload)
		this.guarantee = new Guarantee();

		return {
			id: savedGuarantee.id,
			guaranteeId: savedGuarantee.guaranteeId,
			requestDate: savedGuarantee.requestDate
		};
	}

	async requestCertificate(guaranteeId: string) {
		const url = new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }/certificate-request`);
		await firstValueFrom(this.http.post(url.toString(), null));
	}

	getByDraftStatusFilter() {
		const guaranteeRequestraftStatusField: FieldExpression = {
			field: 'status',
			term: GuaranteeStatus.DRAFT,
			quoted: true
		};
		return luceneToString(guaranteeRequestraftStatusField);
	}

	checkNameUniqueness(name: string) {
		const url = new URL(`${ GUARANTEE_V1_URL }:isNameUnique`);
		url.searchParams.append('name', name.trim());
		return this.http.post<GuaranteeNameUniquenessResponse>(url.toString(), null);
	}

	getDownloadFile(guaranteeId: string, documentId: string) {
		const url = new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }/attachments/${ documentId }`);
		const params = url.searchParams;
		params.append('access_token', this.connectionService.getDownloadAccessToken());
		return url.toString();
	}

	getRequests(
		fields: readonly GuaranteeRequestField[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Guarantee>[] | OrderBy<keyof Guarantee>,
		onlyCount: boolean,
		onlyDistinct: boolean
	) {
		const url = new URL(GUARANTEE_V1_URL);
		const params = url.searchParams;
		// Fields
		params.append('fields', fields.join(','));
		// Filters
		if (filter) {
			params.append('filter', filter);
		}
		// Orderby
		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		params.append('onlyCount', String(onlyCount));
		params.append('distinct', String(onlyDistinct));
		return this.http.get<PaginatedResult<Guarantee>>(url.toString());
	}

	getGuaranteeById(guaranteeId: string) {
		const fields = REQUEST_FIELDS;

		const url = new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));

		return this.http.get<Guarantee>(url.toString())
			.pipe(
				map(guarantee => Object.assign(
					new Guarantee(),
					{
						...guarantee,
						parts: guarantee.parts.map(part => Object.assign(new Part(), part))
					}
				))
			);
	}

	getAllAttachments(id: string) {
		const url = new URL(`${ GUARANTEE_V1_URL }/${ id }/attachments`);
		return	this.http.get<{ results: GuaranteeRequestAttachment[] }>(url.toString());
	}

	async updateGuaranteeAttachmentType(
		guaranteeId: string,
		documentId: string,
		type: AttachmentCategory
	): Promise<AttachmentTypeUpdateResult> {
		const url = new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }/attachments/${ documentId }/type`);
		const response = await firstValueFrom(
			this.http.put<AttachmentTypeUpdateResponse>(url.toString(), { type })
		);
		try {
			const { results } = await firstValueFrom(this.getAllAttachments(guaranteeId));
			return { ...response, attachments: results };
		} catch (refreshError: unknown) {
			return { ...response, refreshError };
		}
	}

	async checkNameUniquenessOrFeedback(name: string): Promise<boolean> {
		this.errorService.resetErrorMsg();
		try {
			const res = await firstValueFrom(this.checkNameUniqueness(name));
			if (!res.success) {
				this.showNameError(res.error);
				return true;
			}
			if (!res.result) {
				this.showNameError(this.translationService.getTR('guarantee.name.unique.reason.duplicate'));
				return true;
			}
			return false;
		} catch (err: unknown) {
			console.error(err);
			this.showNameError(this.translationService.getTR('guarantee.name.unique.reason.network'));
			return true;
		}
	}

	async deleteGuaranteeRequest(guaranteeId: string) {
		const url = new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }`);
		await firstValueFrom(this.http.delete(url.toString()));
	}

	async deleteGuaranteeAttachment(guaranteeId: string, documentId: string) {
		const url = new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }/attachments/${ documentId }`);
		await firstValueFrom(this.http.delete(url.toString()));
	}

	async uploadAttachments(id: string, guarantee: Guarantee) {
		for (const attach of guarantee.attachments) {
			if (attach.chronicleId && attach.deleted) {
				await this.deleteGuaranteeAttachment(id, attach.chronicleId);
				this.guarantee.attachments = this.guarantee.attachments.filter(
					item => item.chronicleId !== attach.chronicleId
				);
				continue;
			}
			const attachment = attach;
			if (!attachment.localUrl) {
				continue;
			}
			const blob = await this.blobService.getBlob(attachment.localUrl);
			const form = new FormData();
			form.append('file', blob, attachment.name);
			form.append('type', attachment.metaData.type || ATTACHMENT_CATEGORY_OTHER);
			const path = await firstValueFrom(this.http.post(
				`${ GUARANTEE_V1_URL }/${ id }/attachments`,
				form,
				{ responseType: 'text' }
			));
			attachment.path = path;
			URL.revokeObjectURL(attachment.localUrl);
			attachment.localUrl = undefined;
		}
	}

	async uploadGuaranteeFile(blob: Blob, filename: string | undefined, id: string) {
		const form = new FormData();
		form.append('file', blob, filename);
		await firstValueFrom(this.http.post(
			`${ CLAIM_SERVER_URL }/v1/guaranteeRequest/upload/${ id }`,
			form,
			{ responseType: 'text' }
		));
	}

	async showAttachment(event: MouseEvent, attachment: GuaranteeRequestAttachment) {
		const { href } = <HTMLAnchorElement> event.currentTarget;
		// ie11 blob url opening
		if (!href.startsWith('blob:') || !('msSaveOrOpenBlob' in navigator)) {
			return;
		}
		event.preventDefault();
		const blob = await this.blobService.getBlob(href);
		navigator.msSaveOrOpenBlob(blob, attachment.name);
	}

	private async syncAttachmentTypeChanges(guaranteeId: string, attachments: GuaranteeRequestAttachment[]) {
		const { results = [] } = await firstValueFrom(this.getAllAttachments(guaranteeId));
		const remoteByChronicleId = new Map<string, GuaranteeRequestAttachment>();
		const remoteByVersionId = new Map<string, GuaranteeRequestAttachment>();
		for (const remoteAttachment of results) {
			if (remoteAttachment.chronicleId) {
				remoteByChronicleId.set(remoteAttachment.chronicleId, remoteAttachment);
			}
			if (remoteAttachment.versionId) {
				remoteByVersionId.set(remoteAttachment.versionId, remoteAttachment);
			}
		}

		for (const attachment of attachments) {
			if (!attachment.chronicleId || attachment.deleted) {
				continue;
			}
			const remoteAttachment = remoteByChronicleId.get(attachment.chronicleId)
				|| remoteByVersionId.get(attachment.chronicleId);
			if (!remoteAttachment?.metaData) {
				continue;
			}
			const localType = attachment.metaData.type || ATTACHMENT_CATEGORY_OTHER;
			const remoteType = remoteAttachment.metaData.type || ATTACHMENT_CATEGORY_OTHER;
			if (localType === remoteType) {
				continue;
			}

			const documentId = remoteAttachment.chronicleId || attachment.chronicleId;
			const url = new URL(`${ GUARANTEE_V1_URL }/${ guaranteeId }/attachments/${ documentId }/type`);
			const update = await firstValueFrom(
				this.http.put<AttachmentTypeUpdateResponse>(url.toString(), { type: localType })
			);
			attachment.metaData.type = update.type;
			attachment.versionId = update.versionId;
			if (remoteAttachment.chronicleId) {
				attachment.chronicleId = remoteAttachment.chronicleId;
			}
		}
	}

	private showNameError(details: string) {
		this.errorService.setErrorMsg(
			this.translationService.getTR('guarantee.name.unique.error', { errordetails: details }),
			'quapp.error'
		);
	}
}
